'use client';

/**
 * ComingSoonPage Component
 *
 * A wrapper component that handles the Coming Soon page display for tenant sites.
 * This component is rendered when a site has comingSoon.enabled = true and
 * the user doesn't have a valid bypass cookie.
 *
 * Features:
 * - Theme-specific Coming Soon rendering (Allys, core, etc.)
 * - Password protection handling
 * - Bypass cookie management
 */

import type { SiteConfig } from '@/lib/api/clients/sites';
import type { Locale } from '@/lib/config/app.config';
import type { ThemeConfig } from '@routeroyal/theme-core';

import { AllysComingSoon } from '@routeroyal/theme-allys';

import { ComingSoon } from './ComingSoon';

interface ComingSoonPageProps {
  /** Site configuration from Route Royal */
  siteConfig: SiteConfig;
  /** Resolved theme configuration */
  theme: ThemeConfig;
  /** Theme ID for conditional rendering */
  themeId: string;
  /** Current locale */
  locale: Locale;
  /** Translation messages */
  messages: Record<string, unknown>;
}

/**
 * ComingSoonPage renders the Coming Soon landing page for sites in development.
 *
 * Both the core ComingSoon and AllysComingSoon components handle password
 * verification internally, including setting bypass cookies and reloading the page.
 */
export function ComingSoonPage({
  siteConfig,
  theme: _theme,
  themeId,
  locale,
  messages,
}: ComingSoonPageProps) {
  // Both theme components handle password verification internally
  const comingSoonConfig = siteConfig.comingSoon!;

  // Build props for Coming Soon component
  const comingSoonProps = {
    brandName: siteConfig.branding?.brandName || siteConfig.name,
    logo: siteConfig.branding?.logo || siteConfig.branding?.logoUrl,
    heading: comingSoonConfig.heading,
    message: comingSoonConfig.message,
    launchDate: comingSoonConfig.launchDate,
    showCountdown: comingSoonConfig.showCountdown,
    contactEmail: comingSoonConfig.contactEmail,
    contactPhone: comingSoonConfig.contactPhone,
    showContactInfo: comingSoonConfig.showContactInfo,
    showSocialLinks: comingSoonConfig.showSocialLinks,
    showNewsletter: comingSoonConfig.showNewsletter,
    backgroundImage: comingSoonConfig.backgroundImage,
    hasPasswordProtection: !!comingSoonConfig.passwordHash,
    organisationId: siteConfig.organisationId,
    locale,
    messages,
  };

  // Render theme-specific Coming Soon component
  // AllysComingSoon handles password verification internally
  if (themeId === 'allys') {
    return <AllysComingSoon {...comingSoonProps} />;
  }

  // Fallback to core Coming Soon component
  return (
    <ComingSoon
      {...comingSoonProps}
      tokens={siteConfig.theme?.tokens}
    />
  );
}
